/**
 * Drives the Fleet Control chat staleness pill in the chat header.
 *
 * The pill renders `{ kind, ageMs }` from `isChatStale(state, now)` and
 * shows the elapsed time. To keep that elapsed counter advancing without
 * a full chat refresh, we tick at 1s while the singleton is mounted.
 * The body is a no-op when the verdict is "fresh" — Lit only re-renders
 * when there is something to show, so the cost is one `isChatStale`
 * call (~O(1) arithmetic) per second.
 */

import type { ChatState } from "../controllers/chat.ts";
import { isChatStale } from "./chat-sync-staleness.ts";

export const CHAT_SYNC_TICK_MS = 1_000;

export type ChatSyncTickerHost = {
  chatSync?: ChatState["chatSync"];
  chatRunId: string | null;
  chatSending: boolean;
  chatStreamStartedAt: number | null;
  connected: boolean;
  chatStalenessTicker: number | null;
  requestUpdate: () => void;
};

/** True exactly while the pill would render something. Cheap. */
export function isChatStalenessVisible(
  host: Pick<ChatSyncTickerHost, "chatSync" | "chatRunId" | "chatSending" | "chatStreamStartedAt" | "connected">,
  nowMs: number,
): boolean {
  return isChatStale(host, nowMs).kind !== "fresh";
}

export function syncChatStalenessTicker(host: ChatSyncTickerHost): void {
  if (host.chatStalenessTicker != null) {
    return;
  }
  host.chatStalenessTicker = window.setInterval(() => {
    if (isChatStalenessVisible(host, Date.now())) {
      host.requestUpdate();
    }
  }, CHAT_SYNC_TICK_MS);
}

export function stopChatStalenessTicker(host: ChatSyncTickerHost): void {
  if (host.chatStalenessTicker != null) {
    clearInterval(host.chatStalenessTicker);
    host.chatStalenessTicker = null;
  }
}
